import type {ShiftingMessage} from "@mytour/shared";
import {numberService} from "@mytour/shared";
import {useState} from "react";
import {type Control, Controller, type FieldValues, type Path} from "react-hook-form";
import {FormFeedback, FormGroup, Input, InputGroup, InputGroupText, Label} from "reactstrap";
import {ProgressiveDisclosureMessage} from "./ProgressiveDisclosureMessage.js";

type FloatingUnitFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  /** Unit suffix displayed inside the input border (e.g. "€", "km") */
  suffix: string;
  control: Control<T>;
  disabled?: boolean;
  /** @deprecated HTML `step` attribute — now a no-op since the input is type="text". Kept to avoid breaking callers. */
  step?: string;
  /** Lower clamp for the arrow-key stepper. */
  min?: number;
  /** Override the arrow key increment. Set to e.g. 1 to increment by 1. */
  arrowStep?: number;
  /** Default value shown grayed-out when field is empty (null) */
  defaultHint?: number | null;
  required?: boolean;
  /**
   * Deux formes possibles (#863) : `{message}` est l'erreur RHF/Zod usuelle
   * (`formState.errors.champ`, un `FormFeedback` simple) ; `ShiftingMessage`
   * (`{short, detail}`) est le contrat de divulgation progressive des
   * générateurs de `@mytour/shared` — rendu via `ProgressiveDisclosureMessage`.
   */
  error?: {message?: string} | ShiftingMessage;
};

function isProgressiveDisclosureError(
  error: {message?: string} | ShiftingMessage
): error is ShiftingMessage {
  return "short" in error;
}

export function FloatingUnitField<T extends FieldValues>({
  name,
  label,
  suffix,
  control,
  disabled,
  min,
  arrowStep,
  defaultHint,
  required,
  error,
}: FloatingUnitFieldProps<T>) {
  // Raw-string buffer while the user is actively typing. `null` means "not buffering,
  // fall back to the form value". This is what lets us display partial input like
  // "0," (mid-decimal) or keep an empty field without snapping back to the committed
  // numeric value.
  const [buffer, setBuffer] = useState<string | null>(null);

  return (
    <InputGroup>
      <FormGroup floating noMargin style={{flexGrow: 1}}>
        <Controller
          name={name}
          control={control}
          render={({field}) => {
            // null in the form means "use default" → show hint grayed out
            const isDefault = field.value == null && defaultHint != null;
            let displayValue: string | number;
            if (buffer !== null) {
              displayValue = buffer;
            } else if (isDefault) {
              displayValue = defaultHint;
            } else {
              displayValue = field.value ?? "";
            }
            return (
              <Input
                {...field}
                value={displayValue}
                // type="text" + inputMode="decimal" lets French users type a comma
                // (iOS Safari strips the comma on type="number" and reports ""). The
                // mobile numeric keyboard is still shown via inputMode.
                type="text"
                inputMode="decimal"
                invalid={!!error}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  setBuffer(rawValue);
                  if (rawValue === "") {
                    // Clearing the field resets to the default sentinel (null when a
                    // defaultHint exists, otherwise 0) — same contract as before.
                    field.onChange(defaultHint != null ? null : 0);
                    return;
                  }
                  const parsed = numberService.parseDecimalInput(rawValue);
                  if (parsed !== null) {
                    field.onChange(parsed);
                  }
                  // When parse fails (e.g. "abc" or a bare separator) we keep the
                  // buffer so the user sees what they typed, but do not propagate a
                  // NaN to the form — the previous numeric value remains committed.
                }}
                onKeyDown={
                  arrowStep
                    ? (e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                          const current = Number(field.value) || 0;
                          const delta = e.key === "ArrowUp" ? arrowStep : -arrowStep;
                          const next = Math.max(min ?? 0, current + delta);
                          field.onChange(next);
                          setBuffer(`${next}`);
                        }
                      }
                    : undefined
                }
                onFocus={() => {
                  // When focusing a defaulted field, clear the buffer so the user
                  // types into an empty input instead of on top of the grayed hint.
                  if (isDefault) {
                    setBuffer("");
                  }
                }}
                onBlur={() => {
                  // Drop the buffer so the display re-syncs with whatever the form
                  // currently holds — including the grayed-out default hint when
                  // the field went back to null.
                  setBuffer(null);
                }}
                disabled={disabled}
                placeholder={label}
                id={`field-${name}`}
                style={isDefault && buffer === null ? {color: "#aaa"} : undefined}
              />
            );
          }}
        />
        <Label for={`field-${name}`}>
          {label}
          {required && " *"}
        </Label>
      </FormGroup>
      <InputGroupText>{suffix}</InputGroupText>
      {error &&
        (isProgressiveDisclosureError(error) ? (
          <ProgressiveDisclosureMessage short={error.short} detail={error.detail} />
        ) : (
          <FormFeedback>{error.message}</FormFeedback>
        ))}
    </InputGroup>
  );
}
